import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import ReactGA from 'react-ga4';

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
if (GA_ID) ReactGA.initialize(GA_ID);

// Always start at top of page on load/refresh
if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (GA_ID) ReactGA.send({ hitType: 'pageview', page: pathname });
  }, [pathname]);
  return null;
}

// Remember a referral link's ?ref= code (and affiliate channel tag, &src=) until
// the visitor signs up, and count the click for affiliate links
function CaptureReferral() {
  const { search } = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(search);
    const ref = params.get('ref');
    if (!ref) return;
    const src = params.get('src');
    saveReferralCode(ref, src);
    if (isNewReferralClick(ref, src)) {
      httpsCallable(functions, 'recordReferralClick')({ code: ref, src }).catch(() => {});
    }
  }, [search]);
  return null;
}
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { StripeProvider } from './contexts/StripeContext';
import Layout from './components/Layout/Layout';
import AppLayout from './components/Layout/AppLayout';
import { hasSessionMarker } from './contexts/sessionMarker';
import { saveReferralCode, isNewReferralClick } from './contexts/referralCode';
import { httpsCallable } from 'firebase/functions';
import { functions } from './config/firebase';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminRoute from './components/Auth/AdminRoute';
import PremiumRoute from './components/Auth/PremiumRoute';

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

// Read once at module load, before Firebase Auth starts restoring the session, so
// it stays stable for the whole initial render.
const bootHadSession = hasSessionMarker();

// Logged-in visitors should land on their dashboard, not the marketing homepage.
// Waiting on `loading` for everyone left signed-out visitors — nearly all homepage
// traffic — with an empty page for the 1-2s Firebase takes to initialise, so we only
// hold the page back when a session probably exists and a redirect is actually
// coming. Without a marker we paint the homepage on the very first render.
const HomeRoute = () => {
  const { currentUser, loading } = useAuth();
  if (currentUser && currentUser.emailVerified) {
    return <Navigate to="/dashboard" replace />;
  }
  if (loading && bootHadSession) return null;
  return <Home />;
};

// Eagerly loaded pages (critical path)
import Home from './pages/Home';
import HomeLoggedIn from './pages/HomeLoggedIn';
import Login from './pages/Auth/Login';
import Signup from './pages/Auth/Signup';
import ForgotPassword from './pages/Auth/ForgotPassword';
import VerifyEmail from './pages/Auth/VerifyEmail';
import NotFound from './pages/NotFound';

// Lazy loaded pages (code splitting)
const Contact = lazy(() => import('./pages/Contact'));
const BookDemo = lazy(() => import('./pages/BookDemo/BookDemo'));
const BookDemoManage = lazy(() => import('./pages/BookDemo/BookDemoManage'));
const FreePlacementTest = lazy(() => import('./pages/FreeTest/FreePlacementTest'));
const Demo = lazy(() => import('./pages/Demo/Demo'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const Resources = lazy(() => import('./pages/Resources/Resources'));
const ResourceDetail = lazy(() => import('./pages/Resources/ResourceDetail'));
const Collections = lazy(() => import('./pages/Resources/Collections'));
const CollectionDetail = lazy(() => import('./pages/Resources/CollectionDetail'));
const BespokeLesson = lazy(() => import('./pages/Bespoke/BespokeLesson'));
const BespokeList = lazy(() => import('./pages/Bespoke/BespokeList'));
const Subscription = lazy(() => import('./pages/Subscription/Subscription'));
const Account = lazy(() => import('./pages/Account/Account'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const AdminResources = lazy(() => import('./pages/Admin/AdminResources'));
const AdminBespoke = lazy(() => import('./pages/Admin/AdminBespoke'));
const AdminUsers = lazy(() => import('./pages/Admin/AdminUsers'));
const AdminCategories = lazy(() => import('./pages/Admin/AdminCategories'));
const AdminEmails = lazy(() => import('./pages/Admin/AdminEmails'));
const AdminOutreach = lazy(() => import('./pages/Admin/AdminOutreach'));
const AdminEmailTemplates = lazy(() => import('./pages/Admin/AdminEmailTemplates'));
const AdminAffiliates = lazy(() => import('./pages/Admin/AdminAffiliates'));
const AdminDemoVideo = lazy(() => import('./pages/Admin/AdminDemoVideo'));
const AdminSchools = lazy(() => import('./pages/Admin/AdminSchools'));
const AffiliateDashboard = lazy(() => import('./pages/Affiliate/AffiliateDashboard'));
const BecomeAffiliate = lazy(() => import('./pages/Affiliate/BecomeAffiliate'));
const AffiliateTerms = lazy(() => import('./pages/Legal/AffiliateTerms'));
const OutreachClip = lazy(() => import('./pages/Admin/OutreachClip'));
const PrivacyPolicy = lazy(() => import('./pages/Legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/Legal/TermsOfService'));
const RefundPolicy = lazy(() => import('./pages/Legal/RefundPolicy'));

// Pathway Builder (lazy loaded)
const PathwayList = lazy(() => import('./pages/Pathway/PathwayList'));
const PathwayCreate = lazy(() => import('./pages/Pathway/PathwayCreate'));
const PathwayDetail = lazy(() => import('./pages/Pathway/PathwayDetail'));
const StudentAssessment = lazy(() => import('./pages/Assessment/StudentAssessment'));
const PathwaySharedView = lazy(() => import('./pages/Pathway/PathwaySharedView'));
const JoinSchool = lazy(() => import('./pages/School/JoinSchool'));
const Schools = lazy(() => import('./pages/Schools/Schools'));

// The School page became the school dashboard; emails and bookmarks still link here
const SchoolRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/dashboard${search}`} replace />;
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <CaptureReferral />
        <AuthProvider>
          <StripeProvider>
            <Routes>
              {/* Public Non-Layout Routes (no Header/Footer) */}
              <Route path="assessment/:token" element={<Suspense fallback={<PageLoader />}><StudentAssessment /></Suspense>} />
              <Route path="pathway/shared/:token" element={<Suspense fallback={<PageLoader />}><PathwaySharedView /></Suspense>} />
              <Route path="learn/:token" element={<Suspense fallback={<PageLoader />}><PathwaySharedView learnerLink /></Suspense>} />
              {/* Popup opened by the Clip to CRM bookmarklet */}
              <Route path="admin/outreach/clip" element={<AdminRoute><Suspense fallback={<PageLoader />}><OutreachClip /></Suspense></AdminRoute>} />

              {/* Public homepage — ships its own nav (.hp-nav) and footer (.hp-footer),
                  so it sits outside Layout. Nesting it there meant Layout's legacy
                  Header/Footer rendered first and only got hidden once Home mounted
                  and added body.homepage-active, flashing the old design on load. */}
              <Route path="/" element={<HomeRoute />} />

              {/* Public Routes — top-nav Layout (auth / legal) */}
              <Route path="/" element={<Layout />}>
                <Route path="login" element={<Login />} />
                <Route path="signup" element={<Signup />} />
                <Route path="forgot-password" element={<ForgotPassword />} />
                <Route path="verify-email" element={<VerifyEmail />} />
                <Route path="contact" element={<Contact />} />
                <Route path="book-a-demo" element={<BookDemo />} />
                <Route path="book-a-demo/manage" element={<BookDemoManage />} />
                <Route path="affiliate" element={<AffiliateDashboard />} />
                <Route path="affiliates" element={<BecomeAffiliate />} />
                <Route path="free-placement-test" element={<FreePlacementTest />} />
                <Route path="demo" element={<Demo key="freelance" audience="freelance" />} />
                <Route path="demo/schools" element={<Demo key="schools" audience="schools" />} />
                <Route path="bespoke-lesson" element={<BespokeLesson />} />
                <Route path="privacy" element={<PrivacyPolicy />} />
                <Route path="terms" element={<TermsOfService />} />
                <Route path="refund" element={<RefundPolicy />} />
                <Route path="affiliate-terms" element={<AffiliateTerms />} />
                <Route path="join-school/:token" element={<Suspense fallback={<PageLoader />}><JoinSchool /></Suspense>} />
                <Route path="schools" element={<Suspense fallback={<PageLoader />}><Schools /></Suspense>} />

                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Route>

              {/* Logged-in Routes — fixed-sidebar AppLayout (Intellibre redesign) */}
              <Route path="/" element={<AppLayout />}>
                {/* Logged-in homepage */}
                <Route path="home" element={<ProtectedRoute><HomeLoggedIn /></ProtectedRoute>} />

                {/* Protected Routes */}
                <Route path="dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
                <Route path="resources/:id" element={<ProtectedRoute><ResourceDetail /></ProtectedRoute>} />
                <Route path="collections" element={<ProtectedRoute><Collections /></ProtectedRoute>} />
                <Route path="collections/:id" element={<ProtectedRoute><CollectionDetail /></ProtectedRoute>} />
                <Route path="bespoke" element={<ProtectedRoute><BespokeList /></ProtectedRoute>} />
                <Route path="bespoke/new" element={<ProtectedRoute><BespokeLesson /></ProtectedRoute>} />
                <Route path="subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
                <Route path="account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                <Route path="school" element={<SchoolRedirect />} />

                {/* Premium Routes (Pathway Builder) */}
                <Route path="pathways" element={<PremiumRoute><PathwayList /></PremiumRoute>} />
                <Route path="pathways/new" element={<PremiumRoute><PathwayCreate /></PremiumRoute>} />
                <Route path="pathways/:id/edit" element={<PremiumRoute><PathwayCreate /></PremiumRoute>} />
                <Route path="pathways/:id" element={<PremiumRoute><PathwayDetail /></PremiumRoute>} />

                {/* Admin Routes */}
                <Route path="admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                <Route path="admin/resources" element={<AdminRoute><AdminResources /></AdminRoute>} />
                <Route path="admin/bespoke" element={<AdminRoute><AdminBespoke /></AdminRoute>} />
                <Route path="admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
                <Route path="admin/categories" element={<AdminRoute><AdminCategories /></AdminRoute>} />
                <Route path="admin/emails" element={<AdminRoute><AdminEmails /></AdminRoute>} />
                <Route path="admin/outreach" element={<AdminRoute><AdminOutreach /></AdminRoute>} />
                <Route path="admin/email-templates" element={<AdminRoute><AdminEmailTemplates /></AdminRoute>} />
                <Route path="admin/affiliates" element={<AdminRoute><AdminAffiliates /></AdminRoute>} />
                <Route path="admin/demo-video" element={<AdminRoute><AdminDemoVideo /></AdminRoute>} />
                <Route path="admin/schools" element={<AdminRoute><AdminSchools /></AdminRoute>} />
              </Route>
            </Routes>
          </StripeProvider>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
